import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../../lib/apiClient';
import { useAuth } from '../../auth/useAuth';
import { IconButton } from '../../components/IconButton';
import { IconAttach, IconDownload, IconTrash } from '../../components/icons';
import MultiListSelect from '../../components/MultiListSelect';
import { labelFor, listOptions, useConfig } from '../../context/ConfigContext';
import { useConfirm } from '../../context/ConfirmContext';
import { downloadFile } from '../../utils/download';

interface PositionOption {
  id: number;
  position_code: string;
  position_title: string;
  org_unit: { unit_name: string };
  pay_grade_id: number;
}

interface PayGradeStepOption {
  id: number;
  step_number: number;
  step_name: string | null;
  salary_amount: string;
}

interface PositionSummary {
  id: number;
  position_code: string;
  position_title: string;
  org_unit: { unit_name: string };
  pay_grade: { name: string };
}

interface EmployeePositionRow {
  id: number;
  start_date: string;
  end_date: string | null;
  position: PositionSummary;
}

interface QualificationRow {
  id: number;
  name: string;
  institution: string;
  graduation_year: number | null;
  level: string;
  has_certificate: boolean;
  certificate_filename: string | null;
}

interface ContactRow {
  id: number;
  name: string;
  relationship: string;
  phone: string;
}

interface DependentRow {
  id: number;
  name: string;
  relationship: string;
  date_of_birth: string | null;
}

interface EmployeeRecord {
  id: number;
  full_name: string;
  first_name: string;
  last_name: string;
  email: string;
  photo_url: string | null;
  mobile: string | null;
  address: string | null;
  gender: string | null;
  date_of_birth: string | null;
  employee_code: string;
  hire_date: string | null;
  termination_date: string | null;
  employment_status_id: number | null;
  bank_name: string | null;
  bank_branch: string | null;
  bank_account_name: string | null;
  bank_account_number: string | null;
  skills: string[];
  licenses: string[];
  memberships: string[];
  current_position: EmployeePositionRow | null;
  positions: EmployeePositionRow[];
  qualifications: QualificationRow[];
  emergency_contacts: ContactRow[];
  dependents: DependentRow[];
}

interface EmployeeFormState {
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  address: string;
  gender: string;
  date_of_birth: string;
  hire_date: string;
  termination_date: string;
  employment_status_id: string | number;
  bank_name: string;
  bank_branch: string;
  bank_account_name: string;
  bank_account_number: string;
  skills: string[];
  licenses: string[];
  memberships: string[];
}

const emptyForm: EmployeeFormState = {
  first_name: '',
  last_name: '',
  email: '',
  mobile: '',
  address: '',
  gender: '',
  date_of_birth: '',
  hire_date: '',
  termination_date: '',
  employment_status_id: '',
  bank_name: '',
  bank_branch: '',
  bank_account_name: '',
  bank_account_number: '',
  skills: [],
  licenses: [],
  memberships: [],
};

const TABS = [
  'details',
  'organization',
  'positions',
  'skills',
  'qualifications',
  'licenses',
  'memberships',
  'emergency-contacts',
  'dependents',
] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  details: 'Details',
  organization: 'Employment',
  positions: 'Positions',
  skills: 'Skills',
  qualifications: 'Qualifications',
  licenses: 'Licenses',
  memberships: 'Memberships',
  'emergency-contacts': 'Emergency Contacts',
  dependents: 'Dependents',
};

// These submit through the single main form/save button; the rest are
// standalone sub-resources with their own dedicated add/remove actions —
// mirrors UserForm.tsx's RECORD_TABS/isNew-guard convention exactly.
const RECORD_TABS: Tab[] = ['details', 'organization', 'skills', 'licenses', 'memberships'];

const emptyQualification = { name: '', institution: '', graduation_year: '', level: '' };
const emptyContact = { name: '', relationship: '', phone: '' };
const emptyDependent = { name: '', relationship: '', date_of_birth: '' };
const emptyAssignment = { position_id: '', pay_grade_step_id: '', change_reason: '', start_date: '', end_date: '' };

export function EmployeeForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lists } = useConfig();
  const { hasPermission } = useAuth();
  const confirmDelete = useConfirm();
  const isNew = !id;
  // An employee.profile.view-only viewer (no hr.update) reaches this same
  // page for their own record — read-only: every mutating control below is
  // hidden, and the main identity/employment fields are disabled rather
  // than removed, so they can still see their own details laid out.
  const canUpdate = hasPermission('hr.update');
  const [tab, setTab] = useState<Tab>('details');
  const [form, setForm] = useState<EmployeeFormState>(emptyForm);
  const [record, setRecord] = useState<EmployeeRecord | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);

  const [positions, setPositions] = useState<PositionOption[]>([]);
  const [assignment, setAssignment] = useState(emptyAssignment);
  const [assignmentErrors, setAssignmentErrors] = useState<Record<string, string[]>>({});
  const [assignmentPayGradeSteps, setAssignmentPayGradeSteps] = useState<PayGradeStepOption[]>([]);

  const [qualification, setQualification] = useState(emptyQualification);
  const [qualificationCertificate, setQualificationCertificate] = useState<File | null>(null);
  const [qualificationErrors, setQualificationErrors] = useState<Record<string, string[]>>({});
  const certificateFileInputRef = useRef<HTMLInputElement | null>(null);
  const certificateUploadTargetRef = useRef<QualificationRow | null>(null);

  const [contact, setContact] = useState(emptyContact);
  const [contactErrors, setContactErrors] = useState<Record<string, string[]>>({});

  const [dependent, setDependent] = useState(emptyDependent);
  const [dependentErrors, setDependentErrors] = useState<Record<string, string[]>>({});

  useEffect(() => {
    api.get<PositionOption[]>('/positions').then((res) => setPositions(res.data));
  }, []);

  // Grade is implied by the chosen position, not picked directly — refetch
  // that grade's steps whenever the selected position changes.
  useEffect(() => {
    const payGradeId = positions.find((p) => String(p.id) === assignment.position_id)?.pay_grade_id;
    if (!payGradeId) {
      setAssignmentPayGradeSteps([]);
      return;
    }
    api.get<PayGradeStepOption[]>(`/pay-grades/${payGradeId}/steps`).then((res) => setAssignmentPayGradeSteps(res.data));
  }, [assignment.position_id, positions]);

  const applyRecord = (data: EmployeeRecord) => {
    setForm({
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      mobile: data.mobile ?? '',
      address: data.address ?? '',
      gender: data.gender ?? '',
      date_of_birth: data.date_of_birth ?? '',
      hire_date: data.hire_date ?? '',
      termination_date: data.termination_date ?? '',
      employment_status_id: data.employment_status_id ?? '',
      bank_name: data.bank_name ?? '',
      bank_branch: data.bank_branch ?? '',
      bank_account_name: data.bank_account_name ?? '',
      bank_account_number: data.bank_account_number ?? '',
      skills: data.skills ?? [],
      licenses: data.licenses ?? [],
      memberships: data.memberships ?? [],
    });
    setRecord(data);
  };

  const reload = () => {
    if (isNew || !id) return Promise.resolve();
    return api.get<EmployeeRecord>(`/employees/${id}`).then((res) => applyRecord(res.data));
  };

  useEffect(() => {
    if (isNew) return;
    reload().then(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  const set = (key: keyof EmployeeFormState) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post<EmployeeRecord>('/employees', form);
        navigate(`/hr/employees/${res.data.id}/edit`);
      } else {
        const res = await api.put<EmployeeRecord>(`/employees/${id}`, form);
        applyRecord(res.data);
      }
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    } finally {
      setSaving(false);
    }
  };

  const assignPosition = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      await api.post(`/employees/${id}/positions`, assignment);
      setAssignment(emptyAssignment);
      setAssignmentErrors({});
      reload();
    } catch (err) {
      setAssignmentErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const addQualification = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      const data = new FormData();
      data.append('name', qualification.name);
      data.append('institution', qualification.institution);
      if (qualification.graduation_year) data.append('graduation_year', String(qualification.graduation_year));
      data.append('level', qualification.level);
      if (qualificationCertificate) data.append('certificate', qualificationCertificate);
      await api.post(`/employees/${id}/qualifications`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setQualification(emptyQualification);
      setQualificationCertificate(null);
      setQualificationErrors({});
      reload();
    } catch (err) {
      setQualificationErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removeQualification = async (row: QualificationRow) => {
    if (!(await confirmDelete(`Remove "${row.name}"?`))) return;
    await api.delete(`/employee-qualifications/${row.id}`);
    reload();
  };

  const pickCertificate = (row: QualificationRow) => {
    certificateUploadTargetRef.current = row;
    certificateFileInputRef.current?.click();
  };

  const onCertificateSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const row = certificateUploadTargetRef.current;
    if (!file || !row) return;

    const data = new FormData();
    data.append('certificate', file);
    try {
      await api.post(`/employee-qualifications/${row.id}/certificate`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setQualificationErrors({});
      reload();
    } catch (err) {
      setQualificationErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Upload failed.'] });
    }
  };

  const downloadCertificate = (row: QualificationRow) =>
    downloadFile(api, `/employee-qualifications/${row.id}/certificate`, row.certificate_filename ?? `${row.name}-certificate`);

  const removeCertificate = async (row: QualificationRow) => {
    if (!(await confirmDelete(`Remove the certificate for "${row.name}"?`))) return;
    await api.delete(`/employee-qualifications/${row.id}/certificate`);
    reload();
  };

  const addContact = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      await api.post(`/employees/${id}/emergency-contacts`, contact);
      setContact(emptyContact);
      setContactErrors({});
      reload();
    } catch (err) {
      setContactErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removeContact = async (row: ContactRow) => {
    if (!(await confirmDelete(`Remove "${row.name}"?`))) return;
    await api.delete(`/employee-emergency-contacts/${row.id}`);
    reload();
  };

  const addDependent = async (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      await api.post(`/employees/${id}/dependents`, dependent);
      setDependent(emptyDependent);
      setDependentErrors({});
      reload();
    } catch (err) {
      setDependentErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removeDependent = async (row: DependentRow) => {
    if (!(await confirmDelete(`Remove "${row.name}"?`))) return;
    await api.delete(`/employee-dependents/${row.id}`);
    reload();
  };

  if (!loaded) return null;

  return (
    <>
      <header className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {!isNew &&
            (record?.photo_url ? (
              <img
                src={record.photo_url}
                alt=""
                style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              />
            ) : (
              <div className="avatar" style={{ width: 48, height: 48, fontSize: '1.1rem' }}>
                {record?.full_name?.charAt(0) ?? '?'}
              </div>
            ))}
          <h1>{isNew ? 'Add employee' : record?.full_name ?? 'Edit employee'}</h1>
        </div>
      </header>

      <div className="toolbar">
        <div className="tab-group">
          {TABS.map((t) => (
            <button key={t} type="button" className={tab === t ? 'tab active' : 'tab'} onClick={() => setTab(t)}>
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        {RECORD_TABS.includes(tab) && (
          <form onSubmit={save}>
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            {/* A fieldset (not display:contents — some browsers don't
                cascade `disabled` through it) carries form-grid itself,
                so disabling it for a read-only viewer greys out and
                disables every field and the Save button in one shot. */}
            <fieldset disabled={!canUpdate} className="form-grid" style={{ border: 'none', padding: 0, margin: 0 }}>

            {tab === 'details' && (
              <>
                <label>
                  Employee Code
                  <input value={record?.employee_code ?? 'Auto-generated on save'} disabled />
                </label>
                <label>
                  First name *
                  <input value={form.first_name} onChange={set('first_name')} required />
                  {errors.first_name && <small className="error">{errors.first_name[0]}</small>}
                </label>
                <label>
                  Last name *
                  <input value={form.last_name} onChange={set('last_name')} required />
                  {errors.last_name && <small className="error">{errors.last_name[0]}</small>}
                </label>
                <label>
                  Email *
                  <input type="email" value={form.email} onChange={set('email')} required />
                  {errors.email && <small className="error">{errors.email[0]}</small>}
                  <small className="muted">Matching an existing System &gt; Users email links that account instead of creating a new one.</small>
                </label>
                <label>
                  Mobile
                  <input value={form.mobile} onChange={set('mobile')} />
                  {errors.mobile && <small className="error">{errors.mobile[0]}</small>}
                </label>
                <label className="span-2">
                  Address
                  <input value={form.address} onChange={set('address')} />
                  {errors.address && <small className="error">{errors.address[0]}</small>}
                </label>
                <label>
                  Gender
                  <select value={form.gender} onChange={set('gender')}>
                    <option value="">—</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </label>
                <label>
                  Date of birth
                  <input type="date" value={form.date_of_birth} onChange={set('date_of_birth')} />
                  {errors.date_of_birth && <small className="error">{errors.date_of_birth[0]}</small>}
                </label>
              </>
            )}

            {tab === 'organization' && (
              <>
                <label>
                  Employment Status *
                  <select value={form.employment_status_id} onChange={set('employment_status_id')} required>
                    <option value="">—</option>
                    {listOptions(lists, 'employment_status').map((o) => (
                      <option key={o.value} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {errors.employment_status_id && <small className="error">{errors.employment_status_id[0]}</small>}
                </label>
                <label>
                  Hire Date
                  <input type="date" value={form.hire_date} onChange={set('hire_date')} />
                  {errors.hire_date && <small className="error">{errors.hire_date[0]}</small>}
                </label>
                <label>
                  Termination Date
                  <input type="date" value={form.termination_date} onChange={set('termination_date')} />
                  {errors.termination_date && <small className="error">{errors.termination_date[0]}</small>}
                </label>
                <label>
                  Bank Name
                  <input value={form.bank_name} onChange={set('bank_name')} />
                  {errors.bank_name && <small className="error">{errors.bank_name[0]}</small>}
                </label>
                <label>
                  Bank Branch
                  <input value={form.bank_branch} onChange={set('bank_branch')} />
                  {errors.bank_branch && <small className="error">{errors.bank_branch[0]}</small>}
                </label>
                <label>
                  Bank Account Name
                  <input value={form.bank_account_name} onChange={set('bank_account_name')} />
                  {errors.bank_account_name && <small className="error">{errors.bank_account_name[0]}</small>}
                </label>
                <label>
                  Bank Account Number
                  <input value={form.bank_account_number} onChange={set('bank_account_number')} />
                  <small className="muted">Used to build the payroll EFT export.</small>
                  {errors.bank_account_number && <small className="error">{errors.bank_account_number[0]}</small>}
                </label>
              </>
            )}

            {tab === 'skills' && (
              <div className="span-2">
                <MultiListSelect listKey="skill" value={form.skills} onChange={(skills) => setForm({ ...form, skills })} />
              </div>
            )}

            {tab === 'licenses' && (
              <div className="span-2">
                <MultiListSelect
                  listKey="license"
                  value={form.licenses}
                  onChange={(licenses) => setForm({ ...form, licenses })}
                />
              </div>
            )}

            {tab === 'memberships' && (
              <div className="span-2">
                <MultiListSelect
                  listKey="membership"
                  value={form.memberships}
                  onChange={(memberships) => setForm({ ...form, memberships })}
                />
              </div>
            )}

            <div className="form-actions span-2">
              <Link className="btn btn-ghost" to="/hr/employees">
                Cancel
              </Link>
              <button className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
            </fieldset>
          </form>
        )}

        {tab === 'positions' &&
          (isNew ? (
            <p className="muted">Save the employee first, then you can assign a position.</p>
          ) : (
            <>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Position</th>
                      <th>Org Unit</th>
                      <th>Pay Grade</th>
                      <th>Start</th>
                      <th>End</th>
                    </tr>
                  </thead>
                  <tbody>
                    {record?.positions.map((row) => (
                      <tr key={row.id}>
                        <td className="muted">{row.position.position_code}</td>
                        <td>{row.position.position_title}</td>
                        <td>{row.position.org_unit?.unit_name}</td>
                        <td className="muted">{row.position.pay_grade?.name}</td>
                        <td className="muted">{row.start_date}</td>
                        <td className="muted">{row.end_date ?? 'Current'}</td>
                      </tr>
                    ))}
                    {record?.positions.length === 0 && (
                      <tr>
                        <td colSpan={6} className="muted center">
                          No positions assigned yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {canUpdate && (
              <form onSubmit={assignPosition} className="form-grid" style={{ marginTop: 16 }}>
                {assignmentErrors.general && <div className="alert alert-error span-2">{assignmentErrors.general[0]}</div>}
                <label>
                  Position *
                  <select
                    value={assignment.position_id}
                    onChange={(e) => setAssignment({ ...assignment, position_id: e.target.value, pay_grade_step_id: '' })}
                    required
                  >
                    <option value="">—</option>
                    {positions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.position_code} — {p.position_title} — {p.org_unit?.unit_name}
                      </option>
                    ))}
                  </select>
                  {assignmentErrors.position_id && <small className="error">{assignmentErrors.position_id[0]}</small>}
                </label>
                <label>
                  Step *
                  <select
                    value={assignment.pay_grade_step_id}
                    onChange={(e) => setAssignment({ ...assignment, pay_grade_step_id: e.target.value })}
                    disabled={!assignment.position_id}
                    required
                  >
                    <option value="">—</option>
                    {assignmentPayGradeSteps.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.step_name ?? `Step ${s.step_number}`} — {s.salary_amount}
                      </option>
                    ))}
                  </select>
                  {assignmentErrors.pay_grade_step_id && <small className="error">{assignmentErrors.pay_grade_step_id[0]}</small>}
                </label>
                <label>
                  Change Reason
                  <select
                    value={assignment.change_reason}
                    onChange={(e) => setAssignment({ ...assignment, change_reason: e.target.value })}
                  >
                    <option value="">—</option>
                    {listOptions(lists, 'compensation_change_reason').map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {assignmentErrors.change_reason && <small className="error">{assignmentErrors.change_reason[0]}</small>}
                </label>
                <label>
                  Start date *
                  <input
                    type="date"
                    value={assignment.start_date}
                    onChange={(e) => setAssignment({ ...assignment, start_date: e.target.value })}
                    required
                  />
                  {assignmentErrors.start_date && <small className="error">{assignmentErrors.start_date[0]}</small>}
                </label>
                <label>
                  End date
                  <input
                    type="date"
                    value={assignment.end_date}
                    onChange={(e) => setAssignment({ ...assignment, end_date: e.target.value })}
                    min={assignment.start_date || undefined}
                  />
                  {assignmentErrors.end_date && <small className="error">{assignmentErrors.end_date[0]}</small>}
                  <small className="muted">Only for a fixed-term assignment with a known expiry — leave blank otherwise.</small>
                </label>
                <div className="form-actions span-2">
                  <button className="btn btn-primary">Assign position</button>
                </div>
              </form>
              )}
            </>
          ))}

        {tab === 'qualifications' &&
          (isNew ? (
            <p className="muted">Save the employee first, then you can add qualifications.</p>
          ) : (
            <>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Institution</th>
                      <th>Graduation Year</th>
                      <th>Level</th>
                      <th>Certificate</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {record?.qualifications.map((row) => (
                      <tr key={row.id}>
                        <td>{row.name}</td>
                        <td>{row.institution}</td>
                        <td className="muted">{row.graduation_year ?? '—'}</td>
                        <td>{labelFor(lists, 'qualification_level', row.level)}</td>
                        <td>
                          {!canUpdate ? (
                            row.has_certificate ? (
                              'Attached'
                            ) : (
                              '—'
                            )
                          ) : row.has_certificate ? (
                            <>
                              <IconButton icon={IconDownload} label="Download certificate" onClick={() => downloadCertificate(row)} />
                              <IconButton
                                icon={IconTrash}
                                label="Remove certificate"
                                variant="danger-ghost"
                                onClick={() => removeCertificate(row)}
                              />
                            </>
                          ) : (
                            <IconButton icon={IconAttach} label="Attach certificate" onClick={() => pickCertificate(row)} />
                          )}
                        </td>
                        <td className="right">
                          {canUpdate && (
                            <IconButton icon={IconTrash} label="Remove" variant="danger-ghost" onClick={() => removeQualification(row)} />
                          )}
                        </td>
                      </tr>
                    ))}
                    {record?.qualifications.length === 0 && (
                      <tr>
                        <td colSpan={6} className="muted center">
                          No qualifications yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <input
                ref={certificateFileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                hidden
                onChange={onCertificateSelected}
              />
              {canUpdate && (
              <form onSubmit={addQualification} className="form-grid" style={{ marginTop: 16 }}>
                {qualificationErrors.general && <div className="alert alert-error span-2">{qualificationErrors.general[0]}</div>}
                <label>
                  Name *
                  <input value={qualification.name} onChange={(e) => setQualification({ ...qualification, name: e.target.value })} required />
                  {qualificationErrors.name && <small className="error">{qualificationErrors.name[0]}</small>}
                </label>
                <label>
                  Institution *
                  <input
                    value={qualification.institution}
                    onChange={(e) => setQualification({ ...qualification, institution: e.target.value })}
                    required
                  />
                  {qualificationErrors.institution && <small className="error">{qualificationErrors.institution[0]}</small>}
                </label>
                <label>
                  Graduation Year
                  <input
                    type="number"
                    value={qualification.graduation_year}
                    onChange={(e) => setQualification({ ...qualification, graduation_year: e.target.value })}
                  />
                  {qualificationErrors.graduation_year && <small className="error">{qualificationErrors.graduation_year[0]}</small>}
                </label>
                <label>
                  Level *
                  <select value={qualification.level} onChange={(e) => setQualification({ ...qualification, level: e.target.value })} required>
                    <option value="">—</option>
                    {listOptions(lists, 'qualification_level').map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {qualificationErrors.level && <small className="error">{qualificationErrors.level[0]}</small>}
                </label>
                <label className="span-2">
                  Certificate
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => setQualificationCertificate(e.target.files?.[0] ?? null)}
                  />
                  {qualificationErrors.certificate && <small className="error">{qualificationErrors.certificate[0]}</small>}
                </label>
                <div className="form-actions span-2">
                  <button className="btn btn-primary">Add qualification</button>
                </div>
              </form>
              )}
            </>
          ))}

        {tab === 'emergency-contacts' &&
          (isNew ? (
            <p className="muted">Save the employee first, then you can add emergency contacts.</p>
          ) : (
            <>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Relationship</th>
                      <th>Phone</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {record?.emergency_contacts.map((row) => (
                      <tr key={row.id}>
                        <td>{row.name}</td>
                        <td>{labelFor(lists, 'relationship', row.relationship)}</td>
                        <td className="muted">{row.phone}</td>
                        <td className="right">
                          {canUpdate && (
                            <IconButton icon={IconTrash} label="Remove" variant="danger-ghost" onClick={() => removeContact(row)} />
                          )}
                        </td>
                      </tr>
                    ))}
                    {record?.emergency_contacts.length === 0 && (
                      <tr>
                        <td colSpan={4} className="muted center">
                          No emergency contacts yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {canUpdate && (
              <form onSubmit={addContact} className="form-grid" style={{ marginTop: 16 }}>
                {contactErrors.general && <div className="alert alert-error span-2">{contactErrors.general[0]}</div>}
                <label>
                  Name *
                  <input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} required />
                  {contactErrors.name && <small className="error">{contactErrors.name[0]}</small>}
                </label>
                <label>
                  Relationship *
                  <select value={contact.relationship} onChange={(e) => setContact({ ...contact, relationship: e.target.value })} required>
                    <option value="">—</option>
                    {listOptions(lists, 'relationship').map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {contactErrors.relationship && <small className="error">{contactErrors.relationship[0]}</small>}
                </label>
                <label>
                  Phone *
                  <input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} required />
                  {contactErrors.phone && <small className="error">{contactErrors.phone[0]}</small>}
                </label>
                <div className="form-actions span-2">
                  <button className="btn btn-primary">Add contact</button>
                </div>
              </form>
              )}
            </>
          ))}

        {tab === 'dependents' &&
          (isNew ? (
            <p className="muted">Save the employee first, then you can add dependents.</p>
          ) : (
            <>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Relationship</th>
                      <th>Date of Birth</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {record?.dependents.map((row) => (
                      <tr key={row.id}>
                        <td>{row.name}</td>
                        <td>{labelFor(lists, 'relationship', row.relationship)}</td>
                        <td className="muted">{row.date_of_birth ?? '—'}</td>
                        <td className="right">
                          {canUpdate && (
                            <IconButton icon={IconTrash} label="Remove" variant="danger-ghost" onClick={() => removeDependent(row)} />
                          )}
                        </td>
                      </tr>
                    ))}
                    {record?.dependents.length === 0 && (
                      <tr>
                        <td colSpan={4} className="muted center">
                          No dependents yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {canUpdate && (
              <form onSubmit={addDependent} className="form-grid" style={{ marginTop: 16 }}>
                {dependentErrors.general && <div className="alert alert-error span-2">{dependentErrors.general[0]}</div>}
                <label>
                  Name *
                  <input value={dependent.name} onChange={(e) => setDependent({ ...dependent, name: e.target.value })} required />
                  {dependentErrors.name && <small className="error">{dependentErrors.name[0]}</small>}
                </label>
                <label>
                  Relationship *
                  <select value={dependent.relationship} onChange={(e) => setDependent({ ...dependent, relationship: e.target.value })} required>
                    <option value="">—</option>
                    {listOptions(lists, 'relationship').map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {dependentErrors.relationship && <small className="error">{dependentErrors.relationship[0]}</small>}
                </label>
                <label>
                  Date of birth
                  <input
                    type="date"
                    value={dependent.date_of_birth}
                    onChange={(e) => setDependent({ ...dependent, date_of_birth: e.target.value })}
                  />
                  {dependentErrors.date_of_birth && <small className="error">{dependentErrors.date_of_birth[0]}</small>}
                </label>
                <div className="form-actions span-2">
                  <button className="btn btn-primary">Add dependent</button>
                </div>
              </form>
              )}
            </>
          ))}
      </div>
    </>
  );
}
